import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDirtyCloseGuard } from "@/lib/hooks/use-dirty-close-guard";
import { usePostHogEvent } from "@/lib/posthog";
import { Rocket } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { ConnectMomentComposeStep } from "./connect-moment-compose-step";
import { ConnectMomentIntroStep } from "./connect-moment-intro-step";
import { CONNECT_CARDSET_ID, useConnectRows } from "./use-connect-rows";
import { useConnectSendFlow } from "./use-connect-send-flow";

const TEXT_PRIMARY = "#2a2a28";
const TEXT_SECONDARY = "#58534c";
const DIVIDER = "#f2f0ec";

interface ConnectMomentSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentorshipId: string;
  teenName: string;
}

export function ConnectMomentSetupDialog({
  open,
  onOpenChange,
  mentorshipId,
  teenName,
}: ConnectMomentSetupDialogProps) {
  // Hoisted so DialogBody's mount-on-open reset doesn't drop the picked mic.
  const [selectedMicId, setSelectedMicId] = useState<string | null>(null);
  if (!open) return null;
  return (
    <DialogBody
      mentorshipId={mentorshipId}
      teenName={teenName}
      selectedMicId={selectedMicId}
      onSelectMic={setSelectedMicId}
      onClose={() => onOpenChange(false)}
    />
  );
}

interface DialogBodyProps {
  mentorshipId: string;
  teenName: string;
  selectedMicId: string | null;
  onSelectMic: (deviceId: string | null) => void;
  onClose: () => void;
}

function DialogBody({
  mentorshipId,
  teenName,
  selectedMicId,
  onSelectMic,
  onClose,
}: DialogBodyProps) {
  const [step, setStep] = useState<"intro" | "compose">("intro");
  const stepRef = useRef(step);
  stepRef.current = step;
  const exitMethodRef = useRef<"x" | "esc">("x");
  const introOpenedAtRef = useRef(performance.now());
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const rows = useConnectRows({ mentorshipId });
  const questionIds = useMemo(
    () => [rows.questions[1].id, rows.questions[2].id, rows.questions[3].id],
    [rows.questions],
  );
  const questionIdsRef = useRef(questionIds);
  questionIdsRef.current = questionIds;
  const rowsHydratedCount = useMemo(
    () =>
      questionIds.reduce(
        (count, id) => count + (rows.libraryByQuestionId[id] ? 1 : 0),
        0,
      ),
    [questionIds, rows.libraryByQuestionId],
  );
  const send = useConnectSendFlow({
    mentorshipId,
    questions: rows.questions,
    canSend: rows.canSend,
    onSent: onClose,
  });

  const handleCloseAttempt = useCallback(() => {
    const exitMethod = exitMethodRef.current;
    const currentStep = stepRef.current;
    if (currentStep === "intro") {
      dispatchEvent("mentor_chat_connect_intro_dismiss", {
        mentorshipId,
        cardsetId: CONNECT_CARDSET_ID,
        timeOnScreenMs: Math.round(
          performance.now() - introOpenedAtRef.current,
        ),
        exitMethod,
      });
    } else if (!send.isPending) {
      const questionsCompleted = questionIdsRef.current.reduce(
        (count, id) => count + (rows.libraryByQuestionId[id] ? 1 : 0),
        0,
      );
      dispatchEvent("mentor_chat_connect_cancel", {
        mentorshipId,
        cardsetId: CONNECT_CARDSET_ID,
        questionsCompleted,
        exitMethod,
      });
    }
    exitMethodRef.current = "x";
    onClose();
  }, [
    dispatchEvent,
    mentorshipId,
    onClose,
    rows.libraryByQuestionId,
    send.isPending,
  ]);

  const close = useDirtyCloseGuard({
    isDirty: rows.isAnyRowRecording,
    onClose: handleCloseAttempt,
  });

  const goCompose = useCallback(() => {
    dispatchEvent("mentor_chat_connect_dialog_view", {
      mentorshipId,
      cardsetId: CONNECT_CARDSET_ID,
      questionIds: questionIdsRef.current,
      rowsHydrated: rowsHydratedCount,
    });
    setStep("compose");
  }, [dispatchEvent, mentorshipId, rowsHydratedCount]);
  const goIntro = useCallback(() => setStep("intro"), []);

  return (
    <>
      <Dialog open onOpenChange={close.openChangeHandler}>
        <DialogContent
          className="flex max-h-[85vh] w-[748px] max-w-[748px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[748px]"
          aria-describedby={undefined}
          onEscapeKeyDown={() => {
            exitMethodRef.current = "esc";
          }}
        >
          <Header />
          {step === "intro" ? (
            <ConnectMomentIntroStep
              mentorshipId={mentorshipId}
              questionIds={questionIds}
              questions={[
                rows.questions[1],
                rows.questions[2],
                rows.questions[3],
              ]}
              onContinue={goCompose}
            />
          ) : (
            <ConnectMomentComposeStep
              mentorshipId={mentorshipId}
              questions={rows.questions}
              libraryByQuestionId={rows.libraryByQuestionId}
              recordingState={rows.recordingState}
              isAnyRowRecording={rows.isAnyRowRecording}
              selectedMicId={selectedMicId}
              onSelectMic={onSelectMic}
              onShuffleAll={rows.onShuffleAll}
              onShuffleOne={rows.onShuffleOne}
              onRecordingStateChange={rows.onRecordingStateChange}
              onLocalBlobChange={rows.onLocalBlobChange}
              onBack={goIntro}
              onSend={send.onSend}
              canSend={rows.canSend}
              isSending={send.isPending}
              sendError={send.error}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        isOpen={rows.shuffleConfirm?.kind === "one"}
        onClose={rows.dismissShuffle}
        onConfirm={rows.confirmShuffle}
        title="Switch to a new question?"
        description="Your recorded answer for this question will stay saved in your library, but it won't be sent to your teen unless you cycle back to this question."
        confirmText="Switch question"
        confirmVariant="default"
      />

      <ConfirmActionDialog
        isOpen={rows.shuffleConfirm?.kind === "all"}
        onClose={rows.dismissShuffle}
        onConfirm={rows.confirmShuffle}
        title="Pick three new questions?"
        description="Your recorded answers stay saved in your library, but the rows on screen will switch to new questions."
        confirmText="New cards"
        confirmVariant="default"
      />

      <ConfirmActionDialog
        isOpen={send.sameDayConfirmOpen}
        onClose={send.dismissSameDay}
        onConfirm={send.confirmSameDay}
        title="Activity already sent today"
        description={`You've already sent an activity to ${teenName} today. Send another?`}
        loading={send.isPending}
        confirmText="Send"
        confirmVariant="default"
      />

      <ConfirmActionDialog
        isOpen={close.confirmOpen}
        onClose={close.dismiss}
        onConfirm={close.confirm}
        title="Stop recording?"
        description="You're recording an answer. Closing now will lose it."
        confirmText="Discard and close"
        confirmVariant="destructive"
      />
    </>
  );
}

function Header() {
  return (
    <div
      className="flex flex-col gap-1 border-b px-6 pb-5 pt-6"
      style={{ borderColor: DIVIDER }}
    >
      <div className="flex items-center gap-2">
        <Rocket
          className="h-5 w-5 shrink-0"
          style={{ color: TEXT_PRIMARY }}
        />
        <DialogTitle
          className="text-xl font-bold tracking-tight"
          style={{ color: TEXT_PRIMARY }}
        >
          Connection cards
        </DialogTitle>
      </div>
      <p className="text-sm" style={{ color: TEXT_SECONDARY }}>
        Questions that actually go somewhere
      </p>
    </div>
  );
}
